import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Region } from '@modules/auth/_components/signup/signup.component';
import { Observable } from 'rxjs';
import { EndpointService } from './endpoint.service';
import {jwtDecode} from 'jwt-decode';

/**
 * To Store cookies Response after validation.
 */
interface CookiesResponse {
  url: string | null;
  tokens: string[] | null;
}

export class TeamActionResponse {
  acknowledge: boolean;
  errorMsg: string;
  response?: InviteValidationResult[];
}

export class InviteValidationResult {
  email: string;
  errorMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private endpointService: EndpointService,
    private http: HttpClient,
  ) { }

  public signIn(username: string , password: string, organization: string): Observable<any> {
    const authorizationData = 'Basic ' + btoa(`${username}:${password}`);
    const httpOptions = {
      headers: new HttpHeaders()
      .set('Authorization', authorizationData)
      .set('organization', organization)
      .set('LoginType', 'Browser')
      .set('Timezone', Intl.DateTimeFormat()
      .resolvedOptions().timeZone),
      observe: 'response' as 'body'
    };
    return this.http.post<any>(this.endpointService.loginUrl(), {}, httpOptions);
  }

  public isUserExits(username: string, orgId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders()
      .set('username', username)
      .set('organization',orgId)
    };
    return this.http.get(this.endpointService.isExits(), httpOptions);
  }

  public isOrganizationExits(companyName: string): Observable<boolean> {
    return this.http.get<boolean>(this.endpointService.isOrganizationExits(), {params:{companyName}});
  }

  public getUserProfilePreference(server: string): Observable<any> {
    return this.http.get(this.endpointService.getUserProfilePreference(server));
  }

  public switchTenant(tid: string , orgid: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders()
      .set('LoginType', 'Browser')
      .set('Timezone', Intl.DateTimeFormat()
      .resolvedOptions().timeZone),
         params:{tid, orgid}
    };
    return this.http.post<any>(this.endpointService.switchTenantUrl(),null, httpOptions);
  }

  public regions(): Observable<Region[]> {
    return this.http.get<Region[]>(this.endpointService.regionsList());
  }

  public checkSignupEmail(email: string, resend: string): Observable<boolean> {
    return this.http.post<boolean>(this.endpointService.checkSignupEmail(), null,{params:{email, resend}});
  }

  public registerOrganization(request: any): Observable<any> {
    return this.http.post(this.endpointService.registerOrganization(), request);
  }

  public validateSignUpLink(link: string): Observable<any> {
    return this.http.post<any>(this.endpointService.validateSignUpLink(), link)
  }

  public resetPasswordSendEmail(username: string , orgId: string): Observable<any> {
    return this.http.post<any>(this.endpointService.resetPasswordSendEmailUrl(), null, {params:{email: username, orgId}});
  }

  public validateResetPassword(link: string): Observable<any> {
    return this.http.post<any>(this.endpointService.validateResetPassword(), link);
  }

  public resetPassword(newPassword: string , confirmPassword: string, link: string): Observable<any> {
    return this.http.post<any>(this.endpointService.resetPasswordUrl(), {newPassword, confirmPassword, link});
  }

  public continueRegistration(host: string , email: string , role: string , request: {}): Observable<any> {
    return this.http.post<any>(this.endpointService.continueRegistrationUrl(host), request, {params:{roleId: role}})
  }

  public sendEmailToAdmin(email: string): Observable<boolean> {
    return this.http.post<boolean>(this.endpointService.sendEmailToAdmin(), null, {params:{email}});
  }

  public createNewUser(origin: string , userPersonalDetails:any): Observable<any>{
    return this.http.post(this.endpointService.createNewUser(origin), userPersonalDetails);
  }

  /**
   * validate existing authorization cookies and redirect to welcome page if already logined with domain..
   */
  public ValidateCookie(): Promise<CookiesResponse | null> {
    return new Promise<CookiesResponse | null>((resolve, reject) => {
      const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
      let currentDomain = window.location.hostname;
      let currentSubDomain = '';
      const domainRegex = /^(?:([^.]+)\.)?([^.\d]+\.[^.]+)$/;
      if (currentDomain) {
        const matches = currentDomain.match(domainRegex);
        if (matches && matches.length) {
          currentSubDomain = matches[1] ? matches[1] : currentSubDomain;
          currentDomain = matches[2] ? matches[2] : currentDomain;
        }
      }
      try {
        for (const cookie of cookies) {
          if (cookie && cookie.startsWith('authorization=') && cookie.includes('=')) {
            const cookieValue = decodeURIComponent(cookie.split('=')[1]);
            const authData = JSON.parse(cookieValue);
            if (
              authData &&
              authData.domain === currentDomain &&
              authData.subdomain === currentSubDomain &&
              authData.jwtToken &&
              authData.jwtRefreshToken
            ) {
              this.validateRefreshToken(authData.jwtRefreshToken)
                .subscribe(
                  (res) => {
                    if (res.body) {
                      const responseBody = res.body;
                      this.clearTokensOfLocalstorage();
                      Object.keys(responseBody).forEach((k) => {
                        localStorage.setItem(k, responseBody[k] || null);
                      });
                      this.generateRedirectUrl().then(res_url => {
                        if (res_url) {
                          const cookiesResponse: CookiesResponse = {
                            url: res_url,
                            tokens: responseBody || null,
                          };
                          resolve(cookiesResponse);
                        }
                      }).catch((error) => {
                        console.error(error);
                        resolve(null);
                      })
                    } else {
                      console.error("Token validation failed.");
                      resolve(null);
                    }
                  },
                  (error) => {
                    console.log(error);
                    resolve(null);
                  }
                );
              return;
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
      resolve(null);
    });
  }

  /**
   * Call service as promise for get sync token validation ..
   * @param token get for validation token ..
   */
  validateRefreshToken(token: string) {
    return this.validateToken(token);
  }

  /**
   * To validate refresh token
   * @param refToken
   * @returns
   */
  public validateToken(refToken: string): Observable<any> {
    const requestUri = this.endpointService.jwtRefresh();
    const authorizationData = 'Bearer ' + refToken;
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: authorizationData,
      }),
      observe: 'response' as const,
    };
    return this.http.post<any>(requestUri, null, httpOptions);
  }

  /**
   * To generate Redirect_Url of Existing User.
   * @returns redirect_url
   */
  public async generateRedirectUrl(): Promise<string | null> {
    try {
      const jwtToken = localStorage.getItem('JWT-TOKEN');
      if (!jwtToken) {
        return null;
      }
      const jwtJson = jwtDecode(jwtToken);
      if (!jwtJson || !jwtJson.sub) {
        return null;
      }
      const userInfo = JSON.parse(jwtJson.sub);
      if (userInfo && userInfo.username && userInfo.orgId) {
        const res = await this.isUserExits(userInfo.username, userInfo.orgId).toPromise();
        const org = res?.orgs[0] || {};
        if (org?.orgId === userInfo?.orgId && org?.tenants) {
          for (const tenant of org.tenants) {
            if (tenant?.tenantId === userInfo?.tenantCode) {
              const redirectUrl = tenant.redirectUrl;
              return redirectUrl;
            }
          }
        }
      }
      return null;
    } catch (error) {
      console.error("Error in generateRedirectUrl", error);
      return null;
    }
  }

  public createNewTenantSchemaByJWT(host: string): Observable<any>{
    return this.http.post<any>(this.endpointService.createNewTenantSchema(host),null);
  }

  public inviteUserUpdatePassword(payload, onboardingToken): Observable<any> {
    if (onboardingToken) {
      const httpOptions = {
        headers: new HttpHeaders({ Authorization: 'Bearer ' + onboardingToken } )
      };
      return this.http.post<any>(this.endpointService.inviteUserUpdatePassword(), payload, httpOptions);
    }
  }

  public updateStatusMembersUrl(server: string, emails: string[], updateAll: boolean, updateType: string, tenantId: string, invitationDto?: string) {
    if(invitationDto) {
      const httpOptions = {
        headers:new HttpHeaders({'authorization-key':invitationDto})
      };
      return this.http.post<TeamActionResponse>(`${this.endpointService.updateStatusMembersUrl(server, updateAll, updateType, tenantId, invitationDto)}`, emails, httpOptions);
    } else {
      return this.http.post<TeamActionResponse>(`${this.endpointService.updateStatusMembersUrl(server, updateAll, updateType, tenantId, invitationDto)}`, emails);
    }
  }

  public validateURL(verifyToken: string, server: string): Observable<any> {
    return this.http.post<any>(this.endpointService.validateURL(server), { key: verifyToken });
  }

  /**
   * Login and access the token ..
   * @param username userid to access the c/login
   * @param tid the user tenant id
   * @param orgId current user orgId
   * @param authorization_key Authorization key for same
   * @returns will return the access token
   */
  public cLogin(username: string, tid: string, orgId: string, authorization_key: string): Observable<any> {
    const httpOptions = {
      headers:{username,'Authorization-key':authorization_key},
      params:{tid,orgId}
    };
    return this.http.post(this.endpointService.cLoginUrl(), null, httpOptions);
  }

  public syncPersonalDetails(firstName: String, lastName: String, redirect_url: string): Observable<any> {
    const payload = {
      fname: firstName,
      lname: lastName
    }
    return this.http.post(this.endpointService.syncPersonalDetails(redirect_url), payload);
  }

  /**
   * To resend the email for newly onboarded user
   * @param server redirect url
   * @param onboardingToken
   * @returns GernalResponseDTO for acknoledgement
   */
  public resendActivationEmail(server: string, onboardingToken: string): Observable<any>{
    const authorizationData = 'Bearer ' + onboardingToken;
    const headers: HttpHeaders = new HttpHeaders({
      Authorization : authorizationData
    });
    return this.http.post(this.endpointService.resendEmail(server), '', {headers});
  }


  /**
   * To remove Tokens related keys from localstorage
   */
  clearTokensOfLocalstorage(){
    localStorage.removeItem('P-JWT-TOKEN');
    localStorage.removeItem('JWT-TOKEN');
    localStorage.removeItem('JWT-REFRESH-TOKEN');
  }

  get getHeaderToken() {
    const token = localStorage.getItem('JWT-REFRESH-TOKEN');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });
  }

  getOrganisation(searchString: string, size: number, page: number) {
    return this.http.get(this.endpointService.getOrganisation(searchString, size, page), { headers: this.getHeaderToken });
  }

  getTetantList(orgId: string) {
    return this.http.get(this.endpointService.getTenantList(orgId), { headers: this.getHeaderToken });
  }

  getTetantFeatures(tenantId: string) {
    return this.http.get(this.endpointService.getTenantFeatures(), { headers: this.getHeaderToken, params: {tenantId} });
  }


  getTetantFeaturesV2(orgId: string) {
    return this.http.get(this.endpointService.getTenantListV2(orgId), { headers: this.getHeaderToken });
  }

  createFeatureFlag(tenantId: string, payload: any) {
    return this.http.post(this.endpointService.createFeatureFlag(tenantId), payload,  { headers: this.getHeaderToken });
  }

  updateFeatureFlag(tenantId: string, payload) {
    return this.http.put(this.endpointService.updateFeatureFlag(tenantId), payload, { headers: this.getHeaderToken });
  }

  checkFeatureFlag(featureFlag: string) {
    const jwtToken = localStorage.getItem('JWT-TOKEN');
    const authorizationData = 'Bearer ' + jwtToken;
    const headers: HttpHeaders = new HttpHeaders({
      Authorization : authorizationData
    });
    return this.http.get(this.endpointService.checkFeatureFlag(featureFlag), { headers });
  }

  public syncDetailsForManualUserCreation(firstName: String, lastName: String, redirect_url: string): Observable<any> {
    const payload = {
      fname: firstName,
      lname: lastName
    }
    return this.http.post(this.endpointService.syncDetailsForManualUserCreation(redirect_url), payload);
  }
}
